import {
  ChangeDetectionStrategy,
  Component,
  input,
  InputSignal,
  OnInit,
  output,
  signal,
  viewChild,
  inject,
} from '@angular/core';
import { DrawerSetting, GridColumn } from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import { DataType } from '@shared/helpers/enums/data-type';
import { QueryOptions } from 'odata-query';
import {
  Collection,
  EntityType,
  GridItemCreateEvent,
  GridItemUpdateEvent,
} from '../../models';
import { AdminPrimeGridComponent } from '../admin-prime-grid/admin-prime-grid.component';
import { UpsertEntityTypeComponent } from '../upsert-entity-type/upsert-entity-type.component';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-entity-types-grid',
  templateUrl: 'entity-types-grid.component.html',
})
export class EntityTypesGridComponent implements OnInit {
  private translateService = inject(TranslateService);

  source: InputSignal<Collection<EntityType>> = input.required({});

  query = input<Partial<QueryOptions<EntityType>>>();
  queryWritable = signal<Partial<QueryOptions<EntityType>>>({});

  queryChange = output<Partial<QueryOptions<EntityType>>>();
  create = output<GridItemCreateEvent<EntityType>>();
  update = output<GridItemUpdateEvent<EntityType>>();

  actionMenuTitle = this.translateService.instant(
    'admin.grid.actionsColumnName'
  );

  drawerSetting: DrawerSetting<EntityType> = {
    create: {
      title: this.translateService.instant('admin.grid.createEntityTypeTitle'),
      component: UpsertEntityTypeComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.create'),
          command: (closeFn, item): void => {
            if (item?.isValid) {
              this.create.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
    edit: {
      title: this.translateService.instant('admin.grid.updateEntityTypeTitle'),
      component: UpsertEntityTypeComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.update'),
          command: (closeFn, item): void => {
            if (item.isValid) {
              this.update.emit({
                item: item.model!,
                prevItem: item.prevModel,
                onSuccess: closeFn,
              });
            }
          },
          isPrimary: true,
        },
      ],
    },
  };

  searchSetting: SearchSetting<EntityType> = {
    filtersLogicalOperator: 'or',
    filters: [
      {
        dataKey: 'name',
        operator: 'contains',
      },
    ],
  };

  grid = viewChild<AdminPrimeGridComponent<EntityType, EntityType>>('grid');

  actionMenu: ActionButton<EntityType>[] = [
    {
      label: this.translateService.instant('portfolio.actionMenu.edit'),
      icon: '',
      command: (item: EntityType): void => {
        this.grid()?.editActionClick(item);
      },
    },
  ];

  headers: GridColumn[] = [
    {
      label: this.translateService.instant('entityType.name'),
      key: 'name',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('entityType.country'),
      dataType: DataType.String,
      key: 'country.name',
      isSortable: true,
    },
    {
      label: this.translateService.instant('entityType.isDefault'),
      key: 'isDefault',
      dataType: DataType.Boolean,
      isSortable: true,
    },
    {
      label: this.translateService.instant('entityType.created'),
      key: 'created',
      dataType: DataType.Date,
      isSortable: true,
    },
    {
      label: this.translateService.instant('entityType.isActive'),
      key: 'isActive',
      dataType: DataType.Boolean,
      isSortable: true,
    },
  ];

  ngOnInit(): void {
    this.queryWritable.set(this.query() ?? {});
  }

  queryHandler(entityTypeQuery: Partial<QueryOptions<EntityType>>): void {
    this.queryWritable.update(
      (previousQuery: Partial<QueryOptions<EntityType>>) => {
        return {
          ...previousQuery,
          ...entityTypeQuery,
        };
      }
    );
    this.queryChange.emit(this.queryWritable());
  }
}
